import {
  DataModelBuilder, isSameList, isSameData, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { Sector } from './sector.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { SeatingElement } from './seating-element.js';
import { SeatRow } from './seat-row.js';
import { Seat } from './seat.js';
import { SeatingLabel } from './seating-label.js';
import { CameraConstraints } from './camera-constraints.js';
import { SeatingLayoutSerializer } from '../../../serialization/seating-layout-serializer.js';
import { LocalizedString } from '../localized-string.js';
import { VersionRange } from '../../core/version-range.js';
import { CapacityRoom } from './capacity-room.js';

export class SeatingRoom extends CapacityRoom {
  static readonly TYPE_ID: string = 'e166745086c84d0dbc9944a82a8e7ab9';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
      uniqueId: string,
      buildingId: string,
      name: string,
      backgroundImage: string | null,
    public readonly children: SeatingElement[],
    public readonly cameraConstraints: CameraConstraints,
    publicName: LocalizedString | null = null,
  ) {
    super(uniqueId, buildingId, name, backgroundImage, publicName);
  }

  get $typeId(): string {
    return SeatingRoom.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(SeatingRoom.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, data: SerializedData): SeatingRoom {
    const format = context.storage.get<string>('format') ?? 'binary';
    switch(format) {
      case 'json':
        return this.fromJson(context, data);
      case 'binary':
        return this.fromBinary(context, data);
      case 'base64':
        return this.fromBase64(context, data);
    }

    throw Error('Unknown room serialization format: ' + format);
  }

  toMap(context: SerializationContext): SerializedData {
    const format = context.storage.get<string>('format') ?? 'binary';
    switch(format) {
      case 'json':
        return this.toJson(context);
      case 'binary':
        return this.toBinary(context);
      case 'base64':
        return this.toBase64(context);
    }

    throw Error('Unknown room serialization format: ' + format);
  }

  clone(): this {
    return new SeatingRoom(
        this.uniqueId,
        this.buildingId,
        this.name,
        this.backgroundImage,
        this.children.map((e) => e.clone(null)),
        this.cameraConstraints.clone(),
        this.publicName?.clone() ?? null,
    ) as this;
  }

  copyWith({
    publicName = this.publicName,
  }: {
    publicName?: LocalizedString | null,
  } = {}): this {
    return new SeatingRoom(
        this.uniqueId,
        this.buildingId,
        this.name,
        this.backgroundImage,
        this.children.map((e) => e.clone(null)),
        this.cameraConstraints.clone(),
        publicName?.clone() ?? null,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
        this.buildingId === other.buildingId &&
        this.name === other.name &&
        this.backgroundImage === other.backgroundImage &&
        isSameData(this.publicName, other.publicName) &&
        isSameList(this.children, other.children, (a, b) => isSameData(a, b)) &&
        isSameData(this.cameraConstraints, other.cameraConstraints);
  }

  static fromJson(context: SerializationContext, data: SerializedData): SeatingRoom {
    const decodeChild = (childData: SerializedData): SeatingElement => {
      const typeId = MapUtils.getNumber(childData, '$typeId');
      switch(typeId) {
        case Sector.TYPE_ID:
          return Sector.fromJson(childData);
        case SeatRow.TYPE_ID:
          return SeatRow.fromJson(childData);
        case Seat.TYPE_ID:
          return Seat.fromJson(childData);
        case SeatingLabel.TYPE_ID:
          return SeatingLabel.fromJson(childData);
      }

      throw Error(`Unknown SeatingElement of type: ${typeId}`);
    };

    return new SeatingRoom(
        MapUtils.get<string>(data, 'uniqueId'),
        MapUtils.get<string>(data, 'buildingId'),
        MapUtils.get<string>(data, 'name'),
        MapUtils.getOrNull<string>(data, 'backgroundImage'),
        MapUtils.get<SerializedData[]>(data, 'children').map((childData) => decodeChild(childData)),
        CameraConstraints.fromJson(MapUtils.get<SerializedData>(data, 'cameraConstraints')),
        context.fromDataOrNull<LocalizedString>(MapUtils.getOrNull<SerializedData>(data, 'publicName')),
    );
  }

  toJson(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'buildingId': this.buildingId,
      'name': this.name,
      'backgroundImage': this.backgroundImage,
      'children': this.children.map((child) => child.toJson()),
      'cameraConstraints': this.cameraConstraints.toJson(),
      'publicName': context.toDataOrNull(this.publicName),
    };
  }

  static fromBinary(context: SerializationContext, data: SerializedData): SeatingRoom {
    return new SeatingRoom(
        MapUtils.get<string>(data, 'uniqueId'),
        MapUtils.get<string>(data, 'buildingId'),
        MapUtils.get<string>(data, 'name'),
        MapUtils.getOrNull<string>(data, 'backgroundImage'),
        SeatingLayoutSerializer.deserialize(context.fromBinaryBlob(MapUtils.getRaw(data, 'children'))),
        CameraConstraints.fromJson(MapUtils.get<SerializedData>(data, 'cameraConstraints')),
        context.fromDataOrNull<LocalizedString>(MapUtils.getOrNull<SerializedData>(data, 'publicName')),
    );
  }

  toBinary(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'buildingId': this.buildingId,
      'name': this.name,
      'backgroundImage': this.backgroundImage,
      'children': context.toBinaryBlob(SeatingLayoutSerializer.serialize(this.children)),
      'cameraConstraints': this.cameraConstraints.toJson(),
      'publicName': context.toDataOrNull(this.publicName),
    };
  }

  static fromBase64(context: SerializationContext, data: SerializedData): SeatingRoom {
    return new SeatingRoom(
        MapUtils.get<string>(data, 'uniqueId'),
        MapUtils.get<string>(data, 'buildingId'),
        MapUtils.get<string>(data, 'name'),
        MapUtils.getOrNull<string>(data, 'backgroundImage'),
        SeatingLayoutSerializer.deserialize(context.fromBase64(MapUtils.get<string>(data, 'children'))),
        CameraConstraints.fromJson(MapUtils.get<SerializedData>(data, 'cameraConstraints')),
        context.fromDataOrNull<LocalizedString>(MapUtils.getOrNull<SerializedData>(data, 'publicName')),
    );
  }

  toBase64(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'buildingId': this.buildingId,
      'name': this.name,
      'backgroundImage': this.backgroundImage,
      'children': context.toBase64(SeatingLayoutSerializer.serialize(this.children)),
      'cameraConstraints': this.cameraConstraints.toJson(),
      'publicName': context.toDataOrNull(this.publicName),
    };
  }

  static findSeatById(room: SeatingRoom, seatId: number): Seat | null {
    for(const elem of room.children) {
      const seat = this.findSeatByIdRecursively(elem, seatId);
      if(seat != null) {
        return seat;
      }
    }

    return null;
  }

  private static findSeatByIdRecursively(elem: SeatingElement, seatId: number): Seat | null {
    if(elem.$typeId == Sector.TYPE_ID) {
      const sector = elem as Sector;
      for(const child of sector.children) {
        const seat = this.findSeatByIdRecursively(child, seatId);
        if(seat != null) {
          return seat;
        }
      }
    }
    else if(elem.$typeId == SeatRow.TYPE_ID) {
      const row = elem as SeatRow;
      for(const seat of row.seats) {
        if(seat.uniqueId == seatId) {
          return seat;
        }
      }
    }
    else if(elem.$typeId == Seat.TYPE_ID) {
      const seat = elem as Seat;
      if(seat.uniqueId == seatId) {
        return seat;
      }
    }

    return null;
  }
}

export class SeatingRoomBuilder extends DataModelBuilder<SeatingRoom> {
  build(context: SerializationContext, map: SerializedData): SeatingRoom {
    return SeatingRoom.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(SeatingRoom.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(SeatingRoom.SCHEMA_VERSION)) {
      return null;
    }

    let version = versionRange.to;

    // In this version we added the `publicName` field of type `LocalizedString`.
    if(version < 2) {
      map['publicName'] ??= null;
      version ??= 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
